import { Container, Sprite, Text, type Texture } from 'pixi.js';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton } from '../board/BrassButton';
import { animate, COLOURS, displayStyle, prefersReducedMotion, uiStyle } from '../board/ui';
import { glowTexture } from '../road/glow';

/** The streak runs a week; day 7 is the big one. */
const DAYS = 7;

/** The daily bonus board's art, looked up when it opens. */
export interface DailyBonusArt {
  floor: () => Texture | null;
  bezel: () => Texture | null;
  coin: () => Texture | null;
  gift: () => Texture | null;
  check: () => Texture | null;
}

/** Today's bonus as the host sees it. */
export interface DailyBonusOffer {
  /** Today's day in the streak, 1 to 7. */
  today: number;
  /** What each day pays (1 to 7). */
  rewardFor: (day: number) => number;
  /** Claims today's bonus on the wallet; what was credited, or null when
   *  nothing was (already claimed elsewhere, or it failed). */
  claim: () => Promise<{ reward: number; streak: number } | null>;
  /** Coins credited fly from `from` into the coin counter (already in
   *  the wallet: this only shows them going in). */
  fly: (from: { x: number; y: number }, amount: number) => Promise<void>;
}

interface Day {
  holder: Container;
  glow: Sprite;
  bezel: Sprite;
  icon: Sprite;
  tick: Sprite;
  label: Text;
  amount: Text;
}

/**
 * The daily bonus over the slot, on a board (producer 2026-10-14: no plain
 * dialogs; it replaces the purple card with its boxes): the week as seven
 * brass bezels, each with its coins (a gift on day 7), the days done
 * ticked and today glowing; what today pays; and CLAIM, which flies the
 * coins into the coin counter. It closes by itself after.
 */
export class DailyBonusBoard extends BoardDialog {
  private readonly line: Text;
  private readonly days: Day[] = [];
  private readonly rewardText: Text;
  private readonly rewardCoin = new Sprite();
  private readonly claimButton: BrassButton;
  private readonly after: Text;
  private offer: DailyBonusOffer | null = null;
  private today = 1;
  private claimed = false;
  private done: (() => void) | null = null;
  private arranging = false;

  constructor(private readonly art: DailyBonusArt) {
    super({ title: 'DAILY BONUS', floor: art.floor(), maxWidth: 420, titleSize: 18 });
    this.line = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream), align: 'center', wordWrap: true } });
    this.line.anchor.set(0.5, 0);
    this.content.addChild(this.line);
    for (let d = 1; d <= DAYS; d++) {
      const day: Day = {
        holder: new Container(),
        glow: new Sprite(glowTexture()),
        bezel: new Sprite(),
        icon: new Sprite(),
        tick: new Sprite(),
        label: new Text({ text: `DAY ${d}`, style: uiStyle(11, COLOURS.cream, '600') }),
        amount: new Text({ text: '', style: displayStyle(14) }),
      };
      for (const s of [day.glow, day.bezel, day.icon, day.tick]) s.anchor.set(0.5);
      day.glow.blendMode = 'add';
      day.glow.tint = 0xffc457;
      day.label.anchor.set(0.5, 0);
      day.amount.anchor.set(0.5, 0);
      day.holder.addChild(day.glow, day.icon, day.bezel, day.tick, day.label, day.amount);
      this.content.addChild(day.holder);
      this.days.push(day);
    }
    this.rewardText = new Text({ text: '', style: displayStyle(40) });
    this.rewardText.anchor.set(0, 0.5);
    this.rewardCoin.anchor.set(0.5);
    this.claimButton = new BrassButton({ label: 'CLAIM', kind: 'gold', size: 18, onTap: () => void this.doClaim() });
    this.after = new Text({ text: '', style: { ...uiStyle(15, COLOURS.cream), align: 'center', wordWrap: true } });
    this.after.anchor.set(0.5, 0);
    this.content.addChild(this.rewardText, this.rewardCoin, this.claimButton, this.after);
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; today: number; reward: string; buttons: string[]; after: string } {
    return {
      open: this.visible,
      today: this.today,
      reward: this.rewardText.text,
      buttons: this.visible && this.claimButton.visible ? [this.claimButton.labelText] : [],
      after: this.after.visible ? this.after.text : '',
    };
  }

  /** Press a button by its label (browser tests). */
  press(label: string): boolean {
    if (!this.visible || !this.claimButton.visible || this.claimButton.labelText !== label) return false;
    void this.doClaim();
    return true;
  }

  /** Offer today's bonus; resolves when the board has closed. */
  async play(offer: DailyBonusOffer): Promise<void> {
    this.offer = offer;
    this.today = Math.max(1, Math.min(DAYS, offer.today));
    this.claimed = false;
    this.setFloor(this.art.floor());
    this.line.text = this.today === 1 ? 'Come back every day for bigger rewards.' : `Day ${this.today} in a row. Keep it going!`;
    this.rewardText.text = `+${offer.rewardFor(this.today).toLocaleString('en-GB')}`;
    this.claimButton.visible = true;
    this.claimButton.setEnabled(true);
    this.after.visible = false;
    this.paintDays();
    this.arrange();
    const closed = new Promise<void>((resolve) => (this.done = resolve));
    await this.open();
    this.pulseToday();
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private async doClaim(): Promise<void> {
    const offer = this.offer;
    if (!offer || this.claimed) return;
    this.claimed = true;
    this.claimButton.setEnabled(false);
    const result = await offer.claim();
    if (!result) {
      await this.finish();
      return;
    }
    this.today = result.streak;
    this.rewardText.text = `+${result.reward.toLocaleString('en-GB')}`;
    this.paintDays(true);
    this.claimButton.visible = false;
    const tomorrow = Math.min(DAYS, result.streak + 1);
    this.after.text = `Added to your coins. Tomorrow: +${offer.rewardFor(tomorrow).toLocaleString('en-GB')}`;
    this.after.visible = true;
    this.arrange();
    void this.celebrate({ x: this.contentWidth / 2, y: this.rewardText.y });
    await offer.fly(this.rewardCoin.getGlobalPosition(), result.reward);
    await new Promise((resolve) => setTimeout(resolve, 1200));
    await this.finish();
  }

  private async finish(): Promise<void> {
    const done = this.done;
    this.done = null;
    await this.close();
    done?.();
  }

  /** Bezels, coins (a gift on day 7), ticks on the days done, today lit. */
  private paintDays(todayDone = false): void {
    const bezel = this.art.bezel();
    const coin = this.art.coin();
    const gift = this.art.gift();
    const check = this.art.check();
    this.days.forEach((day, i) => {
      const n = i + 1;
      const done = n < this.today || (n === this.today && todayDone);
      const isToday = n === this.today;
      if (bezel) day.bezel.texture = bezel;
      day.bezel.visible = bezel !== null;
      const icon = n === DAYS ? gift ?? coin : coin;
      if (icon) day.icon.texture = icon;
      if (check) day.tick.texture = check;
      day.tick.visible = done && check !== null;
      day.icon.alpha = done && !isToday ? 0.45 : 1;
      day.glow.visible = isToday;
      day.label.text = isToday ? 'TODAY' : `DAY ${n}`;
      day.label.style.fill = isToday ? COLOURS.gold : COLOURS.cream;
      day.amount.text = this.offer ? this.offer.rewardFor(n).toLocaleString('en-GB') : '';
      day.amount.alpha = done && !isToday ? 0.5 : 1;
    });
  }

  private pulseToday(): void {
    if (prefersReducedMotion()) return;
    const day = this.days[this.today - 1];
    if (!day) return;
    const loop = () => {
      if (!this.visible || this.claimed || this.destroyed) return;
      void animate(1200, (t) => {
        day.glow.alpha = 0.55 + 0.45 * Math.sin(t * Math.PI);
      }).then(loop);
    };
    loop();
  }

  private arrange(): void {
    if (this.arranging || !this.after) return;
    this.arranging = true;
    const w = this.contentWidth;
    this.line.style.wordWrapWidth = w - 8;
    this.line.position.set(w / 2, 0);
    let y = this.line.height + 14;
    const slot = w / DAYS;
    const size = Math.min(48, slot - 4);
    this.days.forEach((day, i) => {
      const cx = slot * i + slot / 2;
      const cy = size / 2;
      day.holder.position.set(cx, y);
      fitSprite(day.bezel, size);
      fitSprite(day.icon, size * 0.62);
      fitSprite(day.tick, size * 0.55);
      day.glow.width = day.glow.height = size * 1.9;
      for (const s of [day.glow, day.bezel, day.icon]) s.position.set(0, cy);
      day.tick.position.set(size * 0.22, cy + size * 0.22);
      day.label.position.set(0, size + 4);
      day.amount.position.set(0, day.label.y + day.label.height);
    });
    y += size + 4 + 34 + 16;
    const coinSize = 40;
    const coin = this.art.coin();
    if (coin) this.rewardCoin.texture = coin;
    fitSprite(this.rewardCoin, coinSize);
    const rowW = this.rewardText.width + coinSize + 8;
    this.rewardText.position.set((w - rowW) / 2, y + 22);
    this.rewardCoin.position.set((w - rowW) / 2 + this.rewardText.width + 8 + coinSize / 2, y + 22);
    y += 44 + 14;
    if (this.claimButton.visible) {
      const bw = Math.min(w, 220);
      this.claimButton.resize(bw, 48);
      this.claimButton.position.set((w - bw) / 2, y);
      y += 52;
    } else {
      this.after.style.wordWrapWidth = w - 8;
      this.after.position.set(w / 2, y + 4);
      y += this.after.height + 12;
    }
    this.setContentHeight(y);
    this.arranging = false;
  }
}

function fitSprite(sprite: Sprite, size: number): void {
  const k = size / Math.max(1, sprite.texture.width, sprite.texture.height);
  sprite.scale.set(k);
}
